import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import Navbar from '../../../components/Navbar';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Loaders from '../../../components/Loaders';
import { Switch } from '@mui/material';
import Swal from 'sweetalert2';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

export default function GiftCardCategories() {
    const axiosPrivate = useAxiosPrivate();
    const [data, setData] = useState([]);
    const [editingCategory, setEditingCategory] = useState(null);
    const modalCloseRef = useRef(null);
    const { register, handleSubmit, setValue, reset } = useForm();

    const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: 'btn btn-danger me-3',
        },
        buttonsStyling: false,
    });

    const fetchCategories = async () => {
        const response = await axiosPrivate.get('gift-category/all');
        return response.data.data;
    };

    const { data: allCategories = [], isLoading, refetch } = useQuery({
        queryKey: ['get-gift-categories'],
        queryFn: fetchCategories,
        cacheTime: 5000,
    });

    useEffect(() => {
        if (allCategories.length > 0) {
            setData(allCategories);
        }
    }, [allCategories]);

    const handleToggleActive = useCallback(async (id, value) => {
        await axiosPrivate.put(`gift-category/is_active/${id}`, { is_active: value ? 1 : 0 });
        refetch();
    }, [axiosPrivate, refetch]);

    const handleDelete = useCallback(async (id) => {
        const result = await swalWithBootstrapButtons.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true,
        });

        if (result.isConfirmed) {
            try {
                const res = await axiosPrivate.delete(`gift-category/delete/${id}`);
                if (res.status === 200) {
                    swalWithBootstrapButtons.fire('Deleted!', 'Category has been deleted.', 'success');
                    refetch();
                }
            } catch (error) {
                swalWithBootstrapButtons.fire('Error!', 'There was a problem deleting the category.', 'error');
            }
        }
    }, [axiosPrivate, refetch, swalWithBootstrapButtons]);

    const openEditModal = useCallback((category) => {
        setEditingCategory(category);
        setValue('name_en', category.name_en);
        setValue('name_da', category.name_da);
    }, [setValue]);

    const openCreateModal = useCallback(() => {
        setEditingCategory(null);
        reset({ name_en: '', name_da: '' });
    }, [reset]);

    const onSubmit = async (formData) => {
        try {
            if (editingCategory) {
                await axiosPrivate.put(`gift-category/update/${editingCategory.id}`, {
                    name_en: formData.name_en,
                    name_da: formData.name_da,
                });
            } else {
                await axiosPrivate.post('gift-category/create-new', {
                    name_en: formData.name_en,
                    name_da: formData.name_da,
                });
            }
            modalCloseRef.current?.click();
            refetch();
        } catch (error) {
            console.error('Error saving category:', error);
        }
    };

    const columns = useMemo(() => [
        {
            accessorFn: (row) => row.name_en,
            header: 'Name (EN)',
        },
        {
            accessorFn: (row) => row.name_da,
            header: 'Name (DA)',
        },
        {
            accessorFn: (row) => row.slug,
            header: 'Slug',
        },
        {
            accessorFn: (row) => (
                <Switch
                    checked={!!row.is_active}
                    onChange={(e) => handleToggleActive(row.id, e.target.checked)}
                />
            ),
            header: 'Active',
        },
        {
            accessorFn: (row) => row.sort_order,
            header: 'Sort',
        },
        {
            accessorFn: (row) => (
                <>
                    <button
                        onClick={() => openEditModal(row)}
                        className="btn btn-success mx-2"
                        data-bs-toggle="modal"
                        data-bs-target="#categoryModal"
                    >
                        <i className="fa-regular fa-pen-to-square"></i>
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="btn btn-danger"
                    >
                        <i className="fa-solid fa-trash"></i>
                    </button>
                </>
            ),
            id: 'action',
            header: 'Actions',
        },
    ], [handleToggleActive, handleDelete, openEditModal]);

    const table = useMaterialReactTable({
        columns,
        data,
        enableRowOrdering: true,
        enableSorting: false,
        muiRowDragHandleProps: ({ table }) => ({
            onDragEnd: async () => {
                const { draggingRow, hoveredRow } = table.getState();
                if (hoveredRow && draggingRow) {
                    const newData = [...data];
                    const draggedRow = newData.splice(draggingRow.index, 1)[0];
                    newData.splice(hoveredRow.index, 0, draggedRow);
                    setData(newData);

                    const sortList = newData.map((item, index) => ({
                        id: item.id,
                        sort_order: index,
                    }));

                    try {
                        await axiosPrivate.put('gift-category/sort-alignment', { sort_list: sortList });
                    } catch (error) {
                        console.error('Error updating sort order:', error);
                    }
                }
            },
        }),
    });

    return (
        <>
            <Navbar title="Gift Categories" />
            {isLoading && (
                <div className="d-flex justify-content-center align-items-center">
                    <Loaders />
                </div>
            )}
            {!isLoading && (
                <div className="container my-5">
                    <div className="d-flex justify-content-end mb-3">
                        <button
                            className="btn btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#categoryModal"
                            onClick={openCreateModal}
                        >
                            Add New Category
                        </button>
                    </div>

                    <div className="modal fade" id="categoryModal" tabIndex="-1" aria-labelledby="categoryModalLabel" aria-hidden="true">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="categoryModalLabel">
                                        {editingCategory ? 'Edit Category' : 'Add New Category'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        ref={modalCloseRef}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <div className="mb-3">
                                            <label className="col-form-label">Name (English):</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                {...register('name_en', { required: true })}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="col-form-label">Name (Danish):</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                {...register('name_da', { required: true })}
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-success">
                                            {editingCategory ? 'Update' : 'Create'}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    {data.length > 0 && <MaterialReactTable table={table} />}
                </div>
            )}
        </>
    );
}